import { useCallback, useEffect, useRef, useState } from 'react';
import { getKinnserUploadStatusIntakeIntakeIdKinnserUploadStatusGet } from '../../services/api/sdk.gen';
import type {
  KinnserUploadJob,
  KinnserUploadStatusResponse,
  TaskUploadJobResult,
} from '../../services/api/types.gen';
import { isPushTerminalIntakeStatus } from '../../constants/intakeStatus';
import { useToast } from '../useToastContext';
import type { SurfaceableResultFields } from '../../utils/apiErrorDetail';

const POLL_INTERVAL_MS = 5000;
// ~15 minutes at 5s per attempt. The upload keeps running server-side, and
// polling resumes on reload from the stored job.
const MAX_POLL_ATTEMPTS = 180;

export type KinnserUploadFlow = KinnserUploadJob['flow'];

/** Per-file outcome of an async Kinnser upload, for the result block. */
export interface KinnserUploadFileSummary {
  attachedCount: number;
  /** WellSky's reason for each rejected file. */
  rejectedReasons: string[];
}

/** Result fields the schedule/auto-add hooks add once the upload finishes. */
export interface KinnserUploadResultFields {
  upload_summary?: KinnserUploadFileSummary | null;
}

/**
 * Summarize the upload result. A row WellSky rejected carries its reason in
 * `error`; every other row was attached.
 */
export function summarizeKinnserUpload(
  result: TaskUploadJobResult | null | undefined
): KinnserUploadFileSummary | null {
  if (!result) return null;
  const rows = result.attachments ?? [];
  return {
    attachedCount: rows.filter(row => !row.error).length,
    rejectedReasons: rows.filter(row => row.error).map(row => row.error),
  };
}

/** What the POST returned while the upload was processing, if any. */
interface UploadStartedFields {
  episode_key?: string | null;
  upload_patient_task_key?: string | null;
  upload_task_name?: string | null;
}

/**
 * The result fields both flows share for a completed upload: the task the
 * documents went to and the per-file summary. The stored job answers first;
 * `started` (null after a reload) fills any gap.
 */
export function buildUploadCompletedFields(
  status: KinnserUploadStatusResponse,
  started: UploadStartedFields | null
) {
  const summary = summarizeKinnserUpload(status.result);
  const job = status.job;
  return {
    status: 'completed' as const,
    episode_key: job?.episode_key ?? started?.episode_key,
    attachments_count: summary?.attachedCount ?? null,
    upload_patient_task_key:
      job?.upload_patient_task_key ?? started?.upload_patient_task_key,
    upload_task_name: job?.upload_task_name ?? started?.upload_task_name,
    upload_summary: summary,
  };
}

/**
 * The failure result for a failed upload. A surfaceable server message is
 * shown verbatim; anything else gets `genericMessage`.
 */
export function buildUploadFailureResult(
  status: KinnserUploadStatusResponse,
  genericMessage: string
): { success: false; message: string } & SurfaceableResultFields {
  if (status.surfaceable && status.error_code) {
    return {
      success: false,
      message: status.error || genericMessage,
      surfaceable: true,
      error_code: status.error_code,
    };
  }
  return { success: false, message: genericMessage };
}

interface UseKinnserUploadStatusPollingOptions {
  intakeId: string | undefined;
  /** Global intake status; a terminal status never resumes a poll. */
  intakeStatus?: string | null;
  /** The flow this hook's caller owns; only its own stored job resumes. */
  flow: KinnserUploadFlow;
  /** `intake.kinnser_upload_job`, used to resume polling after a reload. */
  storedJob?: { workflow_id?: string | null; flow?: string | null } | null;
  /** Called once with the terminal status (completed or failed). */
  onDone: (status: KinnserUploadStatusResponse) => void;
}

interface UseKinnserUploadStatusPollingReturn {
  pollingUpload: boolean;
  pollUploadStatus: () => Promise<void>;
}

/**
 * Polls `GET /intake/{id}/kinnser_upload_status` until the async document
 * upload started by schedule/auto-add in Kinnser reaches a terminal state
 * (EMR-3089). Modeled on `pollOnboardStatus` in `useKinnserOnboarding`.
 */
export function useKinnserUploadStatusPolling({
  intakeId,
  intakeStatus,
  flow,
  storedJob,
  onDone,
}: UseKinnserUploadStatusPollingOptions): UseKinnserUploadStatusPollingReturn {
  const [pollingUpload, setPollingUpload] = useState(false);
  const { success: showSuccessToast } = useToast();
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Prevents overlapping poll loops (resume-on-mount vs a fresh start).
  const pollingRef = useRef(false);

  const pollUploadStatus = useCallback(async (): Promise<void> => {
    if (!intakeId || pollingRef.current) return;
    pollingRef.current = true;
    setPollingUpload(true);
    try {
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        if (!isMountedRef.current) return;

        let status: KinnserUploadStatusResponse | undefined;
        try {
          const response =
            await getKinnserUploadStatusIntakeIntakeIdKinnserUploadStatusGet({
              path: { intake_id: intakeId },
              throwOnError: true,
            });
          status = response.data;
        } catch (error) {
          // A failed status fetch means the state was NOT observed: treat it
          // like the server's `unknown` and keep polling (as onboarding does).
          console.error('Error fetching Kinnser upload status:', error);
        }
        if (!isMountedRef.current) return;

        if (status?.status === 'completed' || status?.status === 'failed') {
          onDoneRef.current(status);
          return;
        }
        if (status?.status === 'not_started') {
          // No stored job (cleared after a failure, or never started): the
          // user can push again.
          return;
        }
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      }

      if (!isMountedRef.current) return;
      showSuccessToast(
        'Document upload is still processing in WellSky. Check back shortly.'
      );
    } finally {
      pollingRef.current = false;
      if (isMountedRef.current) {
        setPollingUpload(false);
      }
    }
  }, [intakeId, showSuccessToast]);

  // Resume after a reload while this flow's upload is in flight, at most
  // once per intake per mount. A cleared job (after any failure) never resumes.
  const resumeAttemptedForIntakeRef = useRef<string | null>(null);
  const storedWorkflowId = storedJob?.workflow_id;
  const storedFlow = storedJob?.flow;
  useEffect(() => {
    if (!intakeId || !storedWorkflowId || storedFlow !== flow) return;
    if (isPushTerminalIntakeStatus(intakeStatus) || pollingRef.current) return;
    if (resumeAttemptedForIntakeRef.current === intakeId) return;
    resumeAttemptedForIntakeRef.current = intakeId;
    void pollUploadStatus();
  }, [
    intakeId,
    storedWorkflowId,
    storedFlow,
    flow,
    intakeStatus,
    pollUploadStatus,
  ]);

  return { pollingUpload, pollUploadStatus };
}
